/**
 * MerchantApiClient
 * 
 * Handles merchant profile, tools, and business operations
 */

import { BaseApiClient } from './BaseApiClient';
import type { MerchantProfile } from '../../types/api/merchant';
import type { Address, ContactInfo } from '../../types/api/common';
import type { MerchantAIChatUsageResponse, MerchantAIChatUsageDetailedResponse, AIChatUsageFilters } from '../../types/api/ai-chat';

export class MerchantApiClient extends BaseApiClient {
  /**
   * Get merchant profile (authenticated)
   * Returns the merchant profile in the stable API contract format
   */
  async getMerchantProfile(merchantId?: string): Promise<MerchantProfile> {
    const endpoint = merchantId ? `/merchant/profile/${merchantId}` : '/merchant/profile';
    const response = await this.get<any>(endpoint);
    
    console.log('🔍 [MerchantApiClient] Raw API Response:', JSON.stringify(response, null, 2));
    
    // Check if response is empty or has old format
    if (!response || Object.keys(response).length === 0) {
      console.error('❌ [MerchantApiClient] Empty response from API');
      throw new Error('Merchant profile is empty - backend returned no data');
    }
    
    // Detect which format the API is actually returning
    const hasOldFormat = 'businessName' in response || 'businessType' in response;
    const hasNewFormat = 'name' in response || 'type' in response;
    
    console.log('🔍 [MerchantApiClient] Response format detection:', {
      hasOldFormat,
      hasNewFormat,
      fields: Object.keys(response).slice(0, 10) // First 10 fields
    });
    
    // If backend is returning old format, use it directly (still normalize visibility).
    if (hasOldFormat && !hasNewFormat) {
      console.warn('⚠️ [MerchantApiClient] Backend returning OLD format (businessName, businessType), not documented format');
      const legacy = response as MerchantProfile & {
        settings?: { visibility?: { isPublished?: boolean } };
        status?: { isPublished?: boolean };
      };
      return {
        ...legacy,
        isPublished:
          legacy.isPublished === true ||
          legacy.settings?.visibility?.isPublished === true ||
          legacy.status?.isPublished === true,
      };
    }
    
    // Otherwise map from documented API contract to frontend interface
    console.log('✅ [MerchantApiClient] Mapping from API contract to frontend format');
    const profile: MerchantProfile = {
      id: response.id,
      businessName: response.name, // API returns 'name', we use 'businessName'
      businessType: response.type, // API returns 'type', we use 'businessType'
      description: response.description,
      website: response.contact?.website,
      phone: response.contact?.phone,
      logo: response.media?.logo,
      photos: response.media?.images?.map((img: any) => img.url) || [],
      address: response.location?.components || response.contact?.address ? {
        street: response.location?.components?.street || response.contact?.address,
        city: response.location?.components?.city || '',
        state: response.location?.components?.state || '',
        zipCode: response.location?.components?.postalCode || '',
        country: response.location?.components?.country || ''
      } : undefined,
      settings: {
        acceptCreditCards: true, // These are deprecated, using defaults
        acceptBankTransfers: true,
        acceptPayPal: false,
        requireKYC: false,
        autoAcceptPayments: response.settings?.payments?.autoDeposit ?? true,
      },
      isPublished: response.settings?.visibility?.isPublished === true ||
        response.status?.isPublished === true ||
        response.isPublished === true,
      completedOnboarding: response.status?.completedOnboarding,
      preferredCurrency: response.settings?.currency?.preferred,
      acceptedCurrencies: response.settings?.currency?.accepted,
      socialMedia: response.social,
      googleMapsUrl: response.business?.googleMapsUrl,
      googleMapsEmbedUrl: response.business?.googleMapsEmbedUrl,
      googlePlaceId:
        typeof response.business?.googlePlaceId === 'string'
          ? response.business.googlePlaceId.trim()
          : undefined,
      verification: {
        isVerified: response.status?.verified ?? false,
        documentsSubmitted: false,
        kycStatus: 'pending' as const
      },
      createdAt: response.timestamps?.created || response.createdAt,
      updatedAt: response.timestamps?.updated || response.updatedAt,
      // New fields from refactoring
      mobile: response.contact?.mobile,
      email: response.contact?.email,
      contactName: response.contact?.name,
      images: response.images || [],
      files: response.files || [],
      location: response.location?.coordinates ? {
        type: 'Point',
        coordinates: response.location.coordinates
      } : undefined,
      plusCode: response.location?.plusCode,
      addressComponents: response.location?.components,
      preferences: response.preferences || {
        autoDeposit: response.settings?.payments?.autoDeposit,
        emailOnPayment: response.settings?.notifications?.emailOnPayment,
        showOnMarketplace: response.settings?.visibility?.showOnMarketplace,
        allowGuestCheckout: response.settings?.payments?.allowGuestCheckout,
        requirePhoneVerification: response.settings?.security?.requirePhoneVerification
      },
      // Legacy field for backward compatibility
      guestCheckoutNotifications: response.guestCheckoutNotifications || {
        emailNotifications: response.settings?.notifications?.emailOnPayment,
        autoDeposit: response.settings?.payments?.autoDeposit
      },
      // Fields not in API response, will be undefined
      bankAccount: undefined,
      paypalEmail: undefined,
      qrCode: undefined
    };
    
    console.log('🔍 getMerchantProfile API response:', response);
    console.log('🔍 getMerchantProfile mapped profile:', profile);
    
    return profile;
  }

  /**
   * Fetch multiple public merchant profiles for client-side caching (Contacts, MM$ issuers, etc.).
   * Uses parallel GET /api/v1/public/merchant/{userId} — there is no platform batch route.
   */
  async getPublicMerchantProfilesBatch(merchantIds: string[]): Promise<{
    merchants: MerchantProfile[];
    notFound: string[];
    requested: number;
    returned: number;
  }> {
    const uniqueIds = [...new Set(merchantIds.filter(Boolean))];
    if (uniqueIds.length === 0) {
      return { merchants: [], notFound: [], requested: 0, returned: 0 };
    }

    const merchants: MerchantProfile[] = [];
    const notFound: string[] = [];

    await Promise.all(
      uniqueIds.map(async (id) => {
        try {
          merchants.push(await this.getPublicMerchantProfile(id));
        } catch (error) {
          const msg = error instanceof Error ? error.message : String(error);
          if (
            msg.includes('404') ||
            msg.includes('MERCHANT_PROFILE_HTTP_404') ||
            msg.toLowerCase().includes('not found')
          ) {
            notFound.push(id);
          } else {
            console.warn(
              `[MerchantApiClient] Failed to fetch public merchant profile for ${id}:`,
              error
            );
          }
        }
      })
    );

    return {
      merchants,
      notFound,
      requested: uniqueIds.length,
      returned: merchants.length,
    };
  }

  /**
   * Map API response to MerchantProfile interface
   * Extracted as a helper method for reuse
   */
  private mapApiResponseToMerchantProfile(data: any): MerchantProfile {
    const rawId = data?.id;
    const id =
      rawId == null
        ? ''
        : typeof rawId === 'object' && rawId !== null && '$oid' in rawId && typeof (rawId as any).$oid === 'string'
          ? (rawId as { $oid: string }).$oid
          : String(rawId);

    const galleryImages =
      data.gallery?.images?.map((img: any) => ({
        id: img.id || `img-${Date.now()}-${Math.random()}`,
        url: img.url,
        caption: img.caption || '',
        isPrimary: img.isPrimary || false,
        order: img.displayOrder ?? img.order ?? 0,
        source: img.source || 'upload',
        metadata: img.metadata,
      })) ||
      data.media?.images?.map((img: any) => ({
        id: img.id || `img-${Date.now()}-${Math.random()}`,
        url: img.url,
        caption: img.caption || '',
        isPrimary: img.isPrimary || false,
        order: img.displayOrder || img.order || 0,
        source: img.source || 'upload',
        metadata: img.metadata
      })) ||
      data.images ||
      [];

    return {
      id,
      businessName:
        (typeof data.name === 'string' && data.name.trim()) ||
        (typeof data.businessName === 'string' && data.businessName.trim()) ||
        (typeof data.username === 'string' && data.username.trim()) ||
        'Store',
      businessType: data.type || data.businessType || 'general',
      description: data.description,
      website: data.contact?.website || data.website,
      phone: data.contact?.phone || data.phone,
      logo: data.media?.logo || data.logo,
      photos: data.media?.images?.map((img: any) => img.url) || data.photos || [],
      images: galleryImages,
      catalog: data.catalog,
      gallery: data.gallery
        ? {
            images: galleryImages,
            totalImages: data.gallery.totalImages,
            googleMapsUrl: data.gallery.googleMapsUrl,
          }
        : undefined,
      address: data.location?.components || data.contact?.address ? {
        street: data.location?.components?.street || data.contact?.address,
        city: data.location?.components?.city || data.address?.city || '',
        state: data.location?.components?.state || data.address?.state || '',
        zipCode: data.location?.components?.postalCode || data.address?.zipCode || '',
        country: data.location?.components?.country || data.address?.country || ''
      } : undefined,
      settings: {
        acceptCreditCards: true,
        acceptBankTransfers: true,
        acceptPayPal: false,
        requireKYC: false,
        autoAcceptPayments: data.settings?.payments?.autoDeposit ?? true,
      },
      isPublished: data.settings?.visibility?.isPublished ?? data.status?.isPublished,
      completedOnboarding: data.status?.completedOnboarding,
      preferredCurrency: data.settings?.currency?.preferred || data.preferredCurrency,
      acceptedCurrencies: data.settings?.currency?.accepted || data.acceptedCurrencies,
      socialMedia: data.social || data.socialMedia,
      googleMapsUrl: data.business?.googleMapsUrl || data.gallery?.googleMapsUrl,
      googleMapsEmbedUrl: data.business?.googleMapsEmbedUrl,
      googlePlaceId:
        typeof data.business?.googlePlaceId === 'string'
          ? data.business.googlePlaceId.trim()
          : undefined,
      verification: {
        isVerified: data.status?.verified ?? data.verification?.isVerified ?? false,
        documentsSubmitted: false,
        kycStatus: 'pending' as const
      },
      createdAt: data.timestamps?.created || data.createdAt,
      updatedAt: data.timestamps?.updated || data.updatedAt,
      mobile: data.contact?.mobile || data.mobile,
      email: data.contact?.email || data.email,
      contactName: data.contact?.name || data.contactName,
      files: data.files || [],
      location: data.location?.coordinates ? {
        type: 'Point',
        coordinates: data.location.coordinates
      } : undefined,
      plusCode: data.location?.plusCode,
      addressComponents: data.location?.components,
      preferences: data.preferences || {
        autoDeposit: data.settings?.payments?.autoDeposit,
        emailOnPayment: data.settings?.notifications?.emailOnPayment,
        showOnMarketplace: data.settings?.visibility?.showOnMarketplace,
        allowGuestCheckout: data.settings?.payments?.allowGuestCheckout,
        requirePhoneVerification: data.settings?.security?.requirePhoneVerification
      },
      guestCheckoutNotifications: data.guestCheckoutNotifications || {
        emailNotifications: data.settings?.notifications?.emailOnPayment,
        autoDeposit: data.settings?.payments?.autoDeposit
      },
      mmMoneyBranding: data.settings?.mmMoney
        ? {
            backgroundImageUrl:
              data.settings.mmMoney.backgroundImageUrl ?? data.settings.mmMoney.backgroundUrl,
            currencyIconUrl:
              data.settings.mmMoney.currencyIconUrl ?? data.settings.mmMoney.iconUrl,
          }
        : undefined,
      bankAccount: undefined,
      paypalEmail: undefined,
      qrCode: undefined
    };
  }

  /**
   * Map a public merchant document (GET /public/merchant/:userId, batch, or MERCHANT role payload on user profile)
   * to the app's MerchantProfile shape.
   */
  mapPublicMerchantApiPayload(data: any): MerchantProfile {
    return this.mapApiResponseToMerchantProfile(data);
  }

  /**
   * GET /api/v1/public/merchant/{userId} — owner user id.
   * Uses BaseApiClient so Bearer is sent when logged in: owners can load the public DTO while
   * unpublished; anonymous callers only get published storefronts.
   */
  async getPublicMerchantProfile(userId: string): Promise<MerchantProfile> {
    const endpoint = `/public/merchant/${encodeURIComponent(userId)}`;
    try {
      const data = await this.get<unknown>(endpoint);
      const root = data as Record<string, unknown> | null;
      let payload: unknown = data;
      if (root && typeof root === 'object' && 'merchant' in root) {
        payload = root.merchant;
      }
      if (payload == null || typeof payload !== 'object') {
        throw new Error('MERCHANT_PROFILE_HTTP_404');
      }
      return this.mapApiResponseToMerchantProfile(payload as any);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      const status =
        error !== null &&
        typeof error === 'object' &&
        'status' in error &&
        typeof (error as { status: unknown }).status === 'number'
          ? (error as { status: number }).status
          : undefined;
      const quiet =
        msg.includes('404') ||
        status === 404 ||
        msg === 'MERCHANT_PROFILE_INVALID_JSON' ||
        msg.startsWith('MERCHANT_PROFILE_HTTP_');
      if (!quiet) {
        console.error('Error fetching public merchant profile:', error);
      }
      if (status === 404) {
        throw new Error('MERCHANT_PROFILE_HTTP_404');
      }
      if (status !== undefined && status >= 400) {
        throw new Error(`MERCHANT_PROFILE_HTTP_${status}`);
      }
      if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
        throw new Error('MERCHANT_PROFILE_HTTP_404');
      }
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Update merchant profile (full replacement - use with caution)
   * @deprecated Use patchMerchantProfile for partial updates
   */
  async updateMerchantProfile(updates: Partial<MerchantProfile>): Promise<MerchantProfile> {
    const result = await this.put<{ merchantProfile: MerchantProfile }>('/merchant/profile', updates);
    return result.merchantProfile;
  }

  /**
   * Partially update merchant profile (recommended)
   * Only updates the provided fields, leaving others unchanged
   */
  async patchMerchantProfile(updates: Partial<MerchantProfile>): Promise<MerchantProfile> {
    // Map from frontend interface to API contract format
    const apiUpdates: any = {};
    
    if (updates.businessName !== undefined) apiUpdates.name = updates.businessName;
    if (updates.businessType !== undefined) apiUpdates.type = updates.businessType;
    if (updates.description !== undefined) apiUpdates.description = updates.description;
    
    // Map contact fields - include new fields
    if (updates.phone || updates.website || updates.mobile || updates.email || updates.contactName) {
      apiUpdates.contact = {};
      if (updates.phone) apiUpdates.contact.phone = updates.phone;
      if (updates.website) apiUpdates.contact.website = updates.website;
      if (updates.mobile) apiUpdates.contact.mobile = updates.mobile;
      if (updates.email) apiUpdates.contact.email = updates.email;
      if (updates.contactName) apiUpdates.contact.name = updates.contactName;
    }
    
    // Map address to location - handle new location structure
    if (updates.address || updates.location || updates.plusCode || updates.addressComponents) {
      apiUpdates.location = {};
      
      // Handle addressComponents
      if (updates.addressComponents) {
        apiUpdates.location.components = updates.addressComponents;
      } else if (updates.address) {
        apiUpdates.location.components = {
          street: updates.address.street,
          city: updates.address.city,
          state: updates.address.state,
          postalCode: updates.address.zipCode,
          country: updates.address.country
        };
      }
      
      // Handle GeoJSON location
      if (updates.location) {
        apiUpdates.location.coordinates = updates.location.coordinates;
      }
      
      // Handle Plus Code
      if (updates.plusCode) {
        apiUpdates.location.plusCode = updates.plusCode;
      }
    }
    
    // Map preferences - new structure
    if (updates.preferences) {
      apiUpdates.preferences = updates.preferences;
    }
    
    // Map settings - handle legacy and new format
    if (updates.isPublished !== undefined || updates.preferredCurrency || updates.acceptedCurrencies || updates.guestCheckoutNotifications) {
      apiUpdates.settings = {};
      
      if (updates.isPublished !== undefined) {
        apiUpdates.settings.visibility = {
          isPublished: updates.isPublished,
          showOnMarketplace: updates.isPublished,
        };
      }
      
      if (updates.preferredCurrency || updates.acceptedCurrencies) {
        apiUpdates.settings.currency = {};
        if (updates.preferredCurrency) apiUpdates.settings.currency.preferred = updates.preferredCurrency;
        if (updates.acceptedCurrencies) apiUpdates.settings.currency.accepted = updates.acceptedCurrencies;
      }
      
      // Map legacy guestCheckoutNotifications to new settings structure
      if (updates.guestCheckoutNotifications) {
        apiUpdates.settings.payments = apiUpdates.settings.payments || {};
        apiUpdates.settings.notifications = apiUpdates.settings.notifications || {};
        
        if (updates.guestCheckoutNotifications.autoDeposit !== undefined) {
          apiUpdates.settings.payments.autoDeposit = updates.guestCheckoutNotifications.autoDeposit;
        }
        if (updates.guestCheckoutNotifications.emailNotifications !== undefined) {
          apiUpdates.settings.notifications.emailOnPayment = updates.guestCheckoutNotifications.emailNotifications;
        }
      }
    }
    
    // Map status fields
    if (updates.completedOnboarding !== undefined) {
      apiUpdates.status = { completedOnboarding: updates.completedOnboarding };
    }
    
    // Map social media
    if (updates.socialMedia) {
      apiUpdates.social = updates.socialMedia;
    }
    
    // Map hours
    if (updates.hours) {
      apiUpdates.hours = updates.hours;
    }
    
    // Map images - new field
    if (updates.images) {
      apiUpdates.images = updates.images;
    }
    
    // Map files - new field
    if (updates.files) {
      apiUpdates.files = updates.files;
    }
    
    // Map media - handle legacy photos
    if (updates.logo || updates.photos) {
      apiUpdates.media = {};
      if (updates.logo) apiUpdates.media.logo = updates.logo;
      if (updates.photos) apiUpdates.media.photos = updates.photos;
    }

    // MM$ always-on branding (full-bleed token background + favicon-style icon)
    if (updates.mmMoneyBranding !== undefined) {
      apiUpdates.settings = apiUpdates.settings || {};
      apiUpdates.settings.mmMoney = {
        backgroundImageUrl: updates.mmMoneyBranding.backgroundImageUrl,
        currencyIconUrl: updates.mmMoneyBranding.currencyIconUrl,
      };
    }
    
    console.log('🔍 patchMerchantProfile sending:', apiUpdates);
    
    const response = await this.patch<any>('/merchant/profile', apiUpdates);
    
    // Map the response back to our format
    return this.getMerchantProfile();
  }

  /**
   * Generate merchant QR code
   */
  async generateMerchantQR(): Promise<{ qrCode: string }> {
    return this.post<{ qrCode: string }>('/merchant/qr-code');
  }

  /**
   * Get merchant preferences
   */
  async getMerchantPreferences(): Promise<MerchantProfile['preferences']> {
    return this.get<MerchantProfile['preferences']>('/merchant/preferences');
  }

  /**
   * Update merchant preferences
   */
  async updateMerchantPreferences(preferences: Partial<MerchantProfile['preferences']>): Promise<void> {
    return this.put('/merchant/preferences', preferences);
  }

  /**
   * Get merchant gallery (images)
   */
  async getMerchantGallery(): Promise<{
    merchantId: string;
    images: MerchantProfile['images'];
    totalImages: number;
    googleMapsUrl?: string;
  }> {
    return this.get('/merchant/gallery');
  }

  /**
   * Reorder gallery images
   */
  async reorderGalleryImages(imageIds: string[]): Promise<void> {
    return this.put('/merchant/images/reorder', { imageIds });
  }

  /**
   * Delete gallery image
   */
  async deleteGalleryImage(imageId: string): Promise<void> {
    return this.delete(`/merchant/gallery/${imageId}`);
  }

  /**
   * Set primary/logo image
   */
  async setPrimaryImage(imageId: string, isLogo?: boolean): Promise<void> {
    return this.put(`/merchant/images/${imageId}/primary`, { isLogo });
  }

  /**
   * Search nearby merchants
   */
  async searchNearbyMerchants(params: {
    lat: number;
    lng: number;
    radius?: number;
    businessType?: string;
  }): Promise<{
    merchants: Array<{
      id: string;
      businessName: string;
      address: string;
      location: { type: 'Point'; coordinates: [number, number] };
      plusCode: string;
      distance: number;
      rating?: number;
      logo?: string;
    }>;
    total: number;
    center: { lat: number; lng: number };
    radius: number;
  }> {
    return this.get('/merchants/nearby', { params });
  }

  /**
   * Get merchants for map display
   */
  async getMerchantsForMap(params: {
    north: number;
    south: number;
    east: number;
    west: number;
    businessType?: string;
  }): Promise<{
    merchants: Array<{
      id: string;
      businessName: string;
      coordinates: [number, number];
      plusCode: string;
      rating?: number;
    }>;
    total: number;
    bounds: { north: number; south: number; east: number; west: number };
  }> {
    return this.get('/merchants/map', { params });
  }

  // Business Addresses
  /**
   * Get merchant addresses
   */
  async getMerchantAddresses(): Promise<any[]> {
    const response = await this.get<{ addresses: any[] }>('/merchant/addresses');
    return response.addresses;
  }

  /**
   * Add merchant address
   */
  async addMerchantAddress(addressData: {
    type: 'business' | 'shipping' | 'billing';
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    lat: number;
    lng: number;
    isPrimary?: boolean;
  }): Promise<any> {
    const response = await this.post<{ address: any }>('/merchant/addresses', addressData);
    return response.address;
  }

  /**
   * Update merchant address
   */
  async updateMerchantAddress(addressId: string, updates: any): Promise<{ updated: boolean }> {
    return this.patch<{ updated: boolean }>(`/merchant/addresses/${addressId}`, updates);
  }

  /**
   * Delete merchant address
   */
  async deleteMerchantAddress(addressId: string): Promise<{ deleted: boolean }> {
    return this.delete<{ deleted: boolean }>(`/merchant/addresses/${addressId}`);
  }

  /**
   * Set primary merchant address
   */
  async setPrimaryMerchantAddress(addressId: string): Promise<{ updated: boolean }> {
    return this.post<{ updated: boolean }>(`/merchant/addresses/${addressId}/set-primary`);
  }

  // File Management
  /**
   * Get merchant files
   */
  async getMerchantFiles(): Promise<any[]> {
    const response = await this.get<{ files: any[] }>('/merchant/files');
    return response.files;
  }

  /**
   * Upload merchant file
   * Uses JSON payload with base64-encoded file data (images are compressed first).
   */
  async uploadMerchantFile(file: File): Promise<any> {
    const {
      prepareMerchantFileForUpload,
      merchantFileUploadTooLargeMessage,
    } = await import('@/utils/merchantFileUpload');

    const payload = await prepareMerchantFileForUpload(file);

    try {
      const response = await this.post<{ file: any }>('/merchant/files/upload', payload);
      return response.file;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      const friendly = merchantFileUploadTooLargeMessage(message);
      if (friendly) {
        throw new Error(friendly);
      }
      throw err;
    }
  }

  /**
   * Delete merchant file
   */
  async deleteMerchantFile(fileId: string): Promise<{ deleted: boolean }> {
    return this.delete<{ deleted: boolean }>(`/merchant/files/${fileId}`);
  }

  /**
   * Set merchant logo
   */
  async setMerchantLogo(fileId: string): Promise<{ logoSet: boolean }> {
    return this.post<{ logoSet: boolean }>(`/merchant/files/${fileId}/set-logo`);
  }

  // Stripe Connect
  /**
   * Get merchant Stripe Connect status
   */
  async getMerchantStripeConnectStatus(): Promise<{
    stripeConnectId: string | null;
    status: string;
    capabilities: string[];
    requiresAction: boolean;
    lastUpdated: string | null;
  }> {
    return this.get<{
      stripeConnectId: string | null;
      status: string;
      capabilities: string[];
      requiresAction: boolean;
      lastUpdated: string | null;
    }>('/merchant/stripe-connect/status');
  }

  /**
   * Initiate merchant Stripe Connect
   */
  async initiateMerchantStripeConnect(): Promise<{
    accountId: string;
    onboardingUrl: string;
  }> {
    return this.post<{
      accountId: string;
      onboardingUrl: string;
    }>('/merchant/stripe-connect/initiate');
  }

  /**
   * Disconnect (unlink) the merchant's Stripe Connect account.
   */
  async disconnectMerchantStripeConnect(): Promise<{
    disconnected: boolean;
    previousAccountId: string | null;
    stripeDeauthorized: boolean;
  }> {
    return this.post<{
      disconnected: boolean;
      previousAccountId: string | null;
      stripeDeauthorized: boolean;
    }>('/merchant/stripe-connect/disconnect');
  }

  /**
   * Verify a Stripe Connect account ID
   */
  async verifyStripeConnectId(stripeConnectId: string): Promise<{
    success: boolean;
    stripeConnectId: string;
    verified: boolean;
    accountDetails?: {
      id: string;
      country: string;
      currency: string;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
      capabilities: {
        card_payments: string;
        transfers: string;
      };
      businessType: string;
    };
    message: string;
  }> {
    return this.post<{
      success: boolean;
      stripeConnectId: string;
      verified: boolean;
      accountDetails?: {
        id: string;
        country: string;
        currency: string;
        chargesEnabled: boolean;
        payoutsEnabled: boolean;
        capabilities: {
          card_payments: string;
          transfers: string;
        };
        businessType: string;
      };
      message: string;
    }>('/merchant/stripe-connect/verify', { stripeConnectId });
  }
  
  /**
   * Legacy shape for callers that expect verify-style fields.
   * Uses GET /merchant/stripe-connect/status (not /id).
   */
  async getStripeConnectId(): Promise<{
    stripeConnectId: string | null;
    verified: boolean;
    verifiedAt: string | null;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    country: string | null;
    currency: string | null;
  }> {
    const s = await this.getMerchantStripeConnectStatus();
    const connected = s.status === 'connected';
    return {
      stripeConnectId: s.stripeConnectId,
      verified: connected,
      verifiedAt: s.lastUpdated,
      chargesEnabled: connected,
      payoutsEnabled: connected,
      country: null,
      currency: null,
    };
  }

  /**
   * Get merchant Stripe Connect onboarding URL
   */
  async getMerchantStripeConnectOnboardingUrl(): Promise<{ onboardingUrl: string }> {
    return this.get<{ onboardingUrl: string }>('/merchant/stripe-connect/onboarding-url');
  }

  // QR Code Management
  /**
   * Get merchant QR codes
   */
  async getMerchantQRCodes(): Promise<any[]> {
    const response = await this.get<{ qrCodes: any[] }>('/merchant/qr-codes');
    return response.qrCodes;
  }

  /**
   * Generate merchant QR code
   */
  async generateMerchantQRCode(qrData?: {
    type?: string;
    amount?: number;
    currency?: string;
    message?: string;
    merchantId?: string;
    url?: string;
  }): Promise<any> {
    const response = await this.post<{ qrCode: any }>('/merchant/qr-codes/generate', qrData || {});
    return response.qrCode;
  }

  /**
   * Delete merchant QR code
   */
  async deleteMerchantQRCode(qrId: string): Promise<{ deleted: boolean }> {
    return this.delete<{ deleted: boolean }>(`/merchant/qr-codes/${qrId}`);
  }

  // Guest Checkout
  /**
   * Process guest payment
   */
  async processGuestPayment(paymentData: {
    amount: number;
    currency: string;
    description: string;
    merchantId: string;
    paymentMethod: 'paypal' | 'stripe';
    customerEmail?: string;
    customerName?: string;
  }): Promise<{ confirmationCode: string; transactionId: string }> {
    return this.post<{ confirmationCode: string; transactionId: string }>('/guest/payment', paymentData);
  }

  /**
   * Notify merchant of guest payment
   */
  async notifyMerchantOfGuestPayment(notificationData: {
    merchantId: string;
    confirmationCode: string;
    amount: number;
    currency: string;
    paymentMethod: string;
  }): Promise<void> {
    await this.post('/merchant/guest-payment-notification', notificationData);
  }

  /**
   * Update merchant settings
   */
  async updateMerchantSettings(settings: {
    guestCheckoutNotifications?: {
      emailNotifications: boolean;
      autoDeposit: boolean;
      requireConfirmation: boolean;
    };
  }): Promise<void> {
    await this.put('/merchant/settings', settings);
  }

  // Merchant Network and Growth
  /**
   * Request merchant
   */
  async requestMerchant(merchantData: {
    name?: string;
    id?: string;
    location?: string;
    category?: string;
  }): Promise<{ requestId: string; message: string }> {
    return this.post<{ requestId: string; message: string }>('/merchant/request', merchantData);
  }

  /**
   * Search merchants
   */
  async searchMerchants(searchData: {
    term?: string;
    location?: string;
    category?: string;
    lat?: number;
    lng?: number;
    radius?: number;
    verified?: boolean;
    sort?: 'relevance' | 'name' | 'rating' | 'newest';
    limit?: number;
    skip?: number;
  } = {}): Promise<any> {
    const params = new URLSearchParams();
    
    // Add all optional parameters if provided
    if (searchData.term !== undefined) params.append('term', searchData.term);
    if (searchData.location) params.append('location', searchData.location);
    if (searchData.category) params.append('category', searchData.category);
    if (searchData.lat !== undefined) params.append('lat', searchData.lat.toString());
    if (searchData.lng !== undefined) params.append('lng', searchData.lng.toString());
    if (searchData.radius !== undefined) params.append('radius', searchData.radius.toString());
    if (searchData.verified !== undefined) params.append('verified', searchData.verified.toString());
    if (searchData.sort) params.append('sort', searchData.sort);
    if (searchData.limit !== undefined) params.append('limit', searchData.limit.toString());
    if (searchData.skip !== undefined) params.append('skip', searchData.skip.toString());

    const response = await this.get<any>(`/merchant/search?${params.toString()}`);
    console.log('MerchantApiClient.searchMerchants: Raw response:', response);
    console.log('MerchantApiClient.searchMerchants: Merchants array:', response.merchants?.length || 'No merchants array');
    console.log('MerchantApiClient.searchMerchants: Pagination:', response.pagination);
    
    // Return the full response to support pagination
    // The API returns { success: true, merchants: [...], pagination: {...} }
    if (response.merchants) {
      return response; // Return full response with pagination
    }
    
    // Fallback for old structure (just array)
    if (Array.isArray(response)) {
      return response;
    }
    
    console.warn('MerchantApiClient.searchMerchants: Unexpected response structure:', response);
    return [];
  }

  /**
   * Get network statistics
   */
  async getNetworkStats(): Promise<{
    totalMerchants: number;
    totalAdvocates: number;
    totalRequests: number;
    successfulOnboardings: number;
    totalRewardsEarned: number;
  }> {
    return this.get('/network/stats');
  }

  /**
   * Get merchant suggestions
   */
  async getMerchantSuggestions(location?: string): Promise<any[]> {
    const params = location ? `?location=${encodeURIComponent(location)}` : '';
    const response = await this.get<{ suggestions: any[] }>(`/merchant/suggestions${params}`);
    return response.suggestions;
  }

  /**
   * Get success stories
   */
  async getSuccessStories(limit: number = 10): Promise<any[]> {
    const response = await this.get<{ stories: any[] }>(`/network/success-stories?limit=${limit}`);
    return response.stories;
  }

  /**
   * Get user rewards
   */
  async getUserRewards(): Promise<{
    totalPoints: number;
    availablePoints: number;
    level: string;
    achievements: any[];
    recentEarnings: any[];
  }> {
    return this.get('/user/rewards');
  }

  /**
   * Get community leaderboard
   */
  async getCommunityLeaderboard(type: 'local' | 'global' = 'global', limit: number = 10): Promise<any[]> {
    const response = await this.get<{ leaderboard: any[] }>(`/community/leaderboard?type=${type}&limit=${limit}`);
    return response.leaderboard;
  }

  /**
   * Get local communities
   */
  async getLocalCommunities(location?: string): Promise<any[]> {
    const params = location ? `?location=${encodeURIComponent(location)}` : '';
    const response = await this.get<{ communities: any[] }>(`/community/local${params}`);
    return response.communities;
  }

  /**
   * Join community
   */
  async joinCommunity(communityId: string): Promise<{ message: string }> {
    return this.post(`/community/${communityId}/join`);
  }

  // Merchant Followers and Announcements
  /**
   * Get merchant followers
   */
  async getMerchantFollowers(): Promise<any[]> {
    return this.get('/merchant/followers');
  }

  /**
   * Remove merchant follower
   */
  async removeMerchantFollower(followerId: string): Promise<void> {
    return this.delete(`/merchant/followers/${followerId}`);
  }

  /**
   * Get merchant announcements
   */
  async getMerchantAnnouncements(): Promise<any[]> {
    return this.get('/merchant/announcements');
  }

  /**
   * Send merchant announcement
   */
  async sendMerchantAnnouncement(data: {
    title: string;
    message: string;
    sendToAll: boolean;
    selectedFollowers?: string[];
  }): Promise<any> {
    return this.post('/merchant/announcements', data);
  }

  /**
   * Get user followed merchants
   */
  async getUserFollowedMerchants(): Promise<any[]> {
    return this.get('/user/followed-merchants');
  }

  /**
   * Unfollow merchant
   */
  async unfollowMerchant(merchantId: string): Promise<void> {
    return this.delete(`/user/followed-merchants/${merchantId}`);
  }

  /**
   * Get AI Chat usage statistics for this merchant
   * GET /api/v1/merchant/ai-chat/usage
   */
  async getAIChatUsageStats(filters?: AIChatUsageFilters): Promise<MerchantAIChatUsageResponse> {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const query = params.toString();
    return this.get<MerchantAIChatUsageResponse>(`/merchant/ai-chat/usage${query ? `?${query}` : ''}`);
  }

  /**
   * Get detailed AI Chat usage statistics with trends
   * GET /api/v1/merchant/ai-chat/usage/detailed
   */
  async getAIChatUsageDetailed(filters?: AIChatUsageFilters): Promise<MerchantAIChatUsageDetailedResponse> {
    const params = new URLSearchParams();
    if (filters?.period) params.append('period', filters.period);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.includeTrends !== undefined) params.append('includeTrends', filters.includeTrends.toString());

    const query = params.toString();
    return this.get<MerchantAIChatUsageDetailedResponse>(`/merchant/ai-chat/usage/detailed${query ? `?${query}` : ''}`);
  }
} 